import debounce from 'lodash.debounce';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import useIsMounted from '@alltrails/core/hooks/useIsMounted';
import { OnSelect, ResultBase, Results } from '../../types/searchBoxTypes';
import FullscreenSearchBox from '../FullscreenSearchBox';

export type CustomSearchBoxProps<T extends ResultBase> = {
  className?: string;
  clearOnSelect?: boolean;
  debounceMs?: number;
  /** Return the request's promise so results show as loading until it settles */
  fetchResultsRequest: (query: string) => void | Promise<void>;
  onHideResults?: () => void;
  /** Called whenever the user's query changes, including when it is cleared */
  onQueryChange?: (query: string) => void;
  onShowResults?: () => void;
  onSelect: OnSelect<T>;
  results: Results<T>;
  validateQuery?: (query: string) => boolean;
  value?: string;
  searchRequestLoading?: boolean;
  afterRenderResults?: () => void;
} & Pick<
  ComponentProps<typeof FullscreenSearchBox<T>>,
  | 'autoFocus'
  | 'dropdownVariant'
  | 'emptyState'
  | 'noResultsEmptyState'
  | 'inputMaxLength'
  | 'onClear'
  | 'onInputBlur'
  | 'onInputFocus'
  | 'placeholder'
  | 'preventScrollOnResultSelection'
  | 'renderAdditionalContent'
  | 'renderResultContent'
  | 'searchResultsTabBar'
  | 'size'
  | 'tertiaryElement'
  | 'usePortal'
  | 'variant'
>;

/**
 * A search box component for UIs that require a result to be selected to commit the search.
 * Ex: Algolia search, where a user must select a result to navigate to the result's page.
 */
const CustomSearchBox = <T extends ResultBase>({
  autoFocus,
  className,
  clearOnSelect,
  debounceMs = 500,
  dropdownVariant,
  emptyState,
  noResultsEmptyState,
  fetchResultsRequest,
  inputMaxLength,
  onClear,
  onHideResults,
  onInputBlur,
  onInputFocus,
  onQueryChange,
  onShowResults,
  onSelect,
  placeholder,
  preventScrollOnResultSelection,
  renderAdditionalContent,
  renderResultContent,
  results,
  searchResultsTabBar,
  size = 'md',
  tertiaryElement,
  usePortal,
  validateQuery,
  searchRequestLoading,
  variant,
  value = '',
  afterRenderResults
}: CustomSearchBoxProps<T>) => {
  const [query, setQuery] = useState(value);
  const isMounted = useIsMounted();
  const [areResultsLoading, setAreResultsLoading] = useState(false);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const search = useCallback(
    (searchQuery: string) => {
      if (isMounted() && (!validateQuery || validateQuery(searchQuery))) {
        return fetchResultsRequest(searchQuery);
      }
      return undefined;
    },
    [fetchResultsRequest, isMounted, validateQuery]
  );

  // Gets an initial set of results and updates results when fetchResults changes
  useEffect(() => {
    const updateResults = async () => {
      setAreResultsLoading(true);
      try {
        await search(query);
      } finally {
        setAreResultsLoading(false);
      }
    };
    updateResults();
    // Don't want to trigger this when query changes
  }, [search]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async text => {
        try {
          await search(text);
        } finally {
          setAreResultsLoading(false);
        }
      }, debounceMs),
    [debounceMs, search]
  );

  const onChange = useCallback(
    (text: string) => {
      setQuery(text);
      onQueryChange?.(text);
      debouncedSearch(text);
      setAreResultsLoading(true);
    },
    [debouncedSearch, onQueryChange]
  );

  const onItemSelect: OnSelect<T> = useCallback(
    (result, additionalInfo) => {
      onSelect(result, additionalInfo);
      if (clearOnSelect) {
        setQuery('');
        onQueryChange?.('');
      }
    },
    [onSelect, clearOnSelect, onQueryChange]
  );

  const onCancelMobileSearch = useCallback(() => {
    setQuery('');
    onQueryChange?.('');
  }, [onQueryChange]);

  return (
    <FullscreenSearchBox
      autoFocus={autoFocus}
      className={className}
      dropdownVariant={dropdownVariant}
      emptyState={emptyState}
      noResultsEmptyState={noResultsEmptyState}
      isLoading={areResultsLoading}
      inputMaxLength={inputMaxLength}
      onClear={onClear}
      onCommit={onItemSelect}
      onHideResults={onHideResults}
      onInputBlur={onInputBlur}
      onInputFocus={onInputFocus}
      onMobileCancel={onCancelMobileSearch}
      onQueryChange={onChange}
      onShowResults={onShowResults}
      placeholder={placeholder}
      preventScrollOnResultSelection={preventScrollOnResultSelection}
      query={query}
      renderAdditionalContent={renderAdditionalContent}
      renderResultContent={renderResultContent}
      requireSelection
      results={results}
      searchResultsTabBar={searchResultsTabBar}
      size={size}
      tertiaryElement={tertiaryElement}
      usePortal={usePortal}
      validateQuery={validateQuery}
      searchRequestLoading={searchRequestLoading}
      variant={variant}
      afterRenderResults={afterRenderResults}
    />
  );
};

export default CustomSearchBox;
